/**
 * Unified Supabase Client Wrapper
 *
 * This module provides a compatibility layer that maps the old singleton
 * interface to the new unified client architecture.
 */

import { getUnifiedClient, signInWithGoogle as unifiedSignInWithGoogle, cleanupPKCEVerifiers, hasActiveSession, getSessionWithRetry } from './supabase/unified-client';

// Re-export the unified client as the default singleton
export const supabase = getUnifiedClient();

// Compatibility function names
export function getSupabaseSingleton() {
  return getUnifiedClient();
}

// OAuth sign-in functions with old interface
export async function signInWithGoogle() {
  // Determine intent from sessionStorage (old behavior)
  const isSignup = sessionStorage.getItem('gjyl-oauth-signup-intent') === 'true';
  return unifiedSignInWithGoogle(isSignup ? 'signup' : 'signin');
}

export async function signInWithApple() {
  const client = getUnifiedClient();
  const getBaseUrl = () => {
    if (import.meta.env.VITE_APP_URL) return import.meta.env.VITE_APP_URL;
    if (typeof window !== 'undefined') return window.location.origin;
    return 'http://localhost:3000';
  };

  // Clean up any stale PKCE verifiers before starting new flow
  cleanupPKCEVerifiers();

  const isSignup = sessionStorage.getItem('gjyl-oauth-signup-intent') === 'true';
  const intent = isSignup ? 'signup' : 'signin';
  const redirectUrl = `${getBaseUrl()}/auth/callback?intent=${intent}`;

  console.log('[OAuth] Starting Apple sign-in with redirectTo:', redirectUrl);

  const { data, error } = await client.auth.signInWithOAuth({
    provider: 'apple',
    options: {
      redirectTo: redirectUrl,
    },
  });

  if (error) {
    console.error('[OAuth] Apple sign-in error:', error);
  }

  return { data, error };
}

// Session management functions
export async function waitForSession(maxMs = 30000) {
  if (typeof window === 'undefined') return null;

  const start = Date.now();
  const client = getUnifiedClient();

  // Quick check first
  const { data: { session } } = await client.auth.getSession();
  if (session?.access_token) {
    // Bind to realtime if needed
    if ((client as any).realtime) {
      (client as any).realtime.setAuth(session.access_token);
    }
    return session;
  }

  // Use retry mechanism
  const { session: retriedSession } = await getSessionWithRetry(
    Math.floor(maxMs / 1000), // Convert to attempts
    1000 // 1 second between attempts
  );

  if (retriedSession?.access_token && (client as any).realtime) {
    (client as any).realtime.setAuth(retriedSession.access_token);
  }

  return retriedSession;
}

// Auth initialization functions for compatibility
export async function initSupabaseAuthOnLoad() {
  if (typeof window === 'undefined') return;

  // Check if already initialized
  if ((window as any).__oauth_bootstrapped) {
    return;
  }

  const client = getUnifiedClient();

  // Check for hash fragments (implicit flow fallback)
  const hashParams = new URLSearchParams(window.location.hash.slice(1));
  const accessToken = hashParams.get('access_token');
  const refreshToken = hashParams.get('refresh_token');

  if (accessToken && refreshToken) {
    console.log('[Auth] Found OAuth tokens in hash, setting session...');
    try {
      const { data, error } = await client.auth.setSession({
        access_token: accessToken,
        refresh_token: refreshToken,
      });
      (window as any).__oauth_bootstrapped = true;
      if (error) {
        console.error('[Auth] setSession from hash failed:', error);
      } else {
        console.log('[Auth] Session established from hash tokens', !!data.session);
        // Clean hash from URL
        window.history.replaceState({}, document.title, window.location.pathname + window.location.search);
      }
    } catch (err) {
      console.error('[Auth] Error setting session from hash:', err);
    }
  }

  // Bind token to Realtime if available
  const { data: { session } } = await client.auth.getSession();
  if (session?.access_token && (client as any).realtime) {
    (client as any).realtime.setAuth(session.access_token);
    console.log('[Auth] Realtime JWT bound (post-init).');
  }
}

export async function bootstrapAuthFromHash() {
  if (typeof window === 'undefined') return false;

  // Only process if we're on a page with hash tokens but not on auth callback pages
  if (window.location.pathname.startsWith('/auth/')) return false;

  const hashParams = new URLSearchParams(window.location.hash.slice(1));
  const accessToken = hashParams.get('access_token');
  const refreshToken = hashParams.get('refresh_token');

  if (accessToken && refreshToken) {
    console.log('[Auth Bootstrap] Found OAuth tokens in hash, setting session immediately...');
    try {
      const client = getUnifiedClient();
      const { data, error } = await client.auth.setSession({
        access_token: accessToken,
        refresh_token: refreshToken,
      });

      if (!error && data.session) {
        console.log('[Auth Bootstrap] Session established, cleaning URL and redirecting...');
        // Clean the hash from URL
        window.history.replaceState({}, document.title, window.location.pathname + window.location.search);

        // Exchange with backend if needed
        try {
          const api = await import('@/services/api').then(m => m.api);
          await api.exchangeSupabase(accessToken);
          console.log('[Auth Bootstrap] Backend session exchanged');
        } catch (err) {
          console.error('[Auth Bootstrap] Backend exchange failed:', err);
        }

        // Redirect via client success page to guarantee session hydration
        window.location.href = '/auth/success?next=%2Fapp';
        return true;
      }
    } catch (err) {
      console.error('[Auth Bootstrap] Error setting session from hash:', err);
    }
  }

  return false;
}

// Export for browser debugging if needed
if (typeof window !== 'undefined' &&
   (!import.meta.env.PROD || import.meta.env.VITE_EXPOSE_SUPABASE === '1')) {
  (window as any).supabase = supabase;
  (window as any).waitForSession = waitForSession;
  (window as any).initSupabaseAuthOnLoad = initSupabaseAuthOnLoad;
  (window as any).bootstrapAuthFromHash = bootstrapAuthFromHash;
  (window as any).cleanupPKCEVerifiers = cleanupPKCEVerifiers;
}
